import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Users, Inbox, Clock, Euro, ChevronRight, CheckSquare, Circle, UserRound, Moon, Tag, BellOff } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Switch } from "../../components/ui/switch";
import { format, parseISO } from "date-fns";
import { nl } from "date-fns/locale";

const eur = (n) => `€ ${Number(n ?? 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [showPrivate, setShowPrivate] = useState(false);

  const load = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleTodo = async (t, e) => {
    e.stopPropagation();
    await api.put(`/admin/todos/${t.id}`, { done: true });
    toast.success(t.scheduled_start ? "To-do afgerond en uit agenda gehaald" : "To-do afgerond");
    load();
  };

  const cards = [
    { label: "Afspraken vandaag", value: stats?.appointments_today, icon: CalendarDays, to: "/admin/agenda" },
    { label: "Deze week", value: stats?.appointments_week, icon: Clock, to: "/admin/agenda" },
    { label: "Klanten", value: stats?.customers, icon: Users, to: "/admin/klanten" },
    { label: "Nieuwe aanvragen", value: (stats?.new_reservations ?? 0) + (stats?.new_contacts ?? 0), icon: Inbox, to: "/admin/aanvragen" },
  ];

  const upcomingItems = (() => {
    const appts = (stats?.upcoming || []).map((a) => ({ ...a, _type: "appt" }));
    if (!showPrivate) return appts;
    const priv = (stats?.upcoming_private || []).map((p) => ({ ...p, _type: "private" }));
    return [...appts, ...priv].sort((a, b) => (a.start || "").localeCompare(b.start || ""));
  })();

  return (
    <div>
      <h1 className="text-3xl font-medium tracking-tight mb-1">Dashboard</h1>
      <p className="text-ink-muted mb-8">Welkom terug, Nelleke.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map((c) => (
          <button key={c.label} data-testid={`stat-${c.label}`} onClick={() => navigate(c.to)}
            className="text-left bg-white rounded-2xl border border-[#E8E2D9] p-6 shadow-[0_8px_30px_rgb(74,63,63,0.04)] transition-all duration-300 hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-[0_16px_40px_rgb(74,63,63,0.10)]">
            <c.icon className="w-5 h-5 text-brand mb-4" strokeWidth={1.5} />
            <div className="text-3xl font-display">{c.value ?? "–"}</div>
            <div className="text-sm text-ink-muted mt-1">{c.label}</div>
          </button>
        ))}
      </div>

      {/* Weekomzet */}
      <div data-testid="week-revenue" className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)] mb-10">
        <div className="flex items-center gap-2 mb-5">
          <Euro className="w-5 h-5 text-brand" strokeWidth={1.5} />
          <h2 className="text-xl font-medium">Waarde deze week</h2>
          <span className="text-xs text-ink-muted ml-auto">Vanaf maandag</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-xl bg-brand-light/50 p-5">
            <div className="text-2xl font-display" data-testid="week-value-done">{eur(stats?.week_value_done)}</div>
            <div className="text-sm text-ink-muted mt-1">Uitgevoerd</div>
          </div>
          <div className="rounded-xl bg-cream-subtle/70 p-5">
            <div className="text-2xl font-display" data-testid="week-value-planned">{eur(stats?.week_value_planned)}</div>
            <div className="text-sm text-ink-muted mt-1">Gepland</div>
          </div>
          <div className="rounded-xl bg-ink text-white p-5">
            <div className="text-2xl font-display" data-testid="week-value-total">{eur(stats?.week_value_total)}</div>
            <div className="text-sm text-white/70 mt-1">Totaal</div>
          </div>
        </div>
      </div>

      {/* Klanten & re-activatie */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
        <div data-testid="dash-dormant" className="bg-white rounded-2xl border border-[#E8E2D9] p-6 shadow-[0_8px_30px_rgb(74,63,63,0.04)] flex flex-col">
          <div className="flex items-center gap-2 mb-3"><Moon className="w-5 h-5 text-brand" strokeWidth={1.5} /><h2 className="text-lg font-medium">Slapende klanten</h2></div>
          <div className="text-4xl font-display" data-testid="dash-dormant-count">{stats?.dormant_count ?? "–"}</div>
          <p className="text-sm text-ink-muted mt-1">Geen afspraak in 6+ maanden.</p>
          {stats?.dormant_count > 0 && (
            <p data-testid="dash-dormant-not-contacted" className="text-sm mt-1 mb-4 inline-flex items-center gap-1.5 text-[#8A6A2F]">
              <BellOff className="w-3.5 h-3.5" strokeWidth={1.75} />
              {stats?.dormant_not_contacted ?? 0} nog niet benaderd
            </p>
          )}
          {!(stats?.dormant_count > 0) && <div className="mb-4" />}
          <button data-testid="dash-winback-btn" onClick={() => navigate("/admin/klanten?winback=1")}
            className="mt-auto self-start rounded-full bg-brand hover:bg-brand-hover text-white text-sm px-4 py-2 transition-colors">Slapend benaderen</button>
        </div>
        <div data-testid="dash-categories" className="bg-white rounded-2xl border border-[#E8E2D9] p-6 shadow-[0_8px_30px_rgb(74,63,63,0.04)] lg:col-span-2">
          <div className="flex items-center gap-2 mb-3"><Tag className="w-5 h-5 text-brand" strokeWidth={1.5} /><h2 className="text-lg font-medium">Klanten per categorie</h2></div>
          {!Object.keys(stats?.customers_by_category || {}).length && <p className="text-sm text-ink-muted">Nog geen categorieën gekoppeld.</p>}
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats?.customers_by_category || {}).sort((a, b) => b[1] - a[1]).map(([cat, n]) => (
              <button key={cat} data-testid={`dash-cat-${cat}`} onClick={() => navigate(`/admin/klanten?categorie=${encodeURIComponent(cat)}`)}
                className="inline-flex items-center gap-2 rounded-full border border-[#E8E2D9] hover:border-brand/50 bg-cream-subtle/40 px-3 py-1.5 text-sm transition-colors">
                <span>{cat}</span>
                <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full bg-brand-light text-brand-hover text-xs font-medium tabular-nums">{n}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* To-Do's deze week (7 dagen rolling) */}
      <div data-testid="dash-week-todos" className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)] mb-10">
        <div className="flex items-center gap-2 mb-5">
          <CheckSquare className="w-5 h-5 text-brand" strokeWidth={1.5} />
          <h2 className="text-xl font-medium">To-Do's deze week</h2>
          <span className="text-xs text-ink-muted ml-auto">Komende 7 dagen</span>
        </div>
        {!stats?.week_todos?.length && (
          <p className="text-ink-muted text-sm">Geen openstaande to-do's met een due-datum de komende 7 dagen.</p>
        )}
        <div className="divide-y divide-[#F2EBE1]">
          {stats?.week_todos?.map((t) => (
            <div key={t.id} data-testid={`dash-todo-${t.id}`}
              className="flex items-center gap-3 py-3 -mx-2 px-2 rounded-lg hover:bg-cream-subtle/40 transition-colors group">
              <button onClick={(e) => toggleTodo(t, e)} data-testid={`dash-todo-done-${t.id}`} className="shrink-0 text-ink-muted hover:text-brand" title="Afvinken">
                <Circle className="w-5 h-5" />
              </button>
              <span className="w-1.5 self-stretch rounded-full shrink-0" style={{ backgroundColor: t.color }} />
              <div className="min-w-0 flex-1 cursor-pointer" onClick={() => navigate("/admin/todos")}>
                <div className="font-medium truncate">{t.title}</div>
                {t.notes && <div className="text-sm text-ink-muted truncate">{t.notes}</div>}
              </div>
              <div className="text-sm text-right text-ink-muted shrink-0 capitalize">
                {format(parseISO(`${t.due_date}T${t.due_time || "00:00"}:00`), t.due_time ? "EEE d MMM · HH:mm" : "EEE d MMM", { locale: nl })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)]">
        <div className="flex items-center justify-between gap-3 mb-5">
          <h2 className="text-xl font-medium">Aankomende afspraken</h2>
          <label className="flex items-center gap-2 text-sm text-ink-muted cursor-pointer select-none" data-testid="dash-show-private-label">
            Privé tonen
            <Switch checked={showPrivate} onCheckedChange={setShowPrivate} data-testid="dash-show-private" />
          </label>
        </div>
        {!upcomingItems.length && <p className="text-ink-muted text-sm">Nog geen afspraken ingepland.</p>}
        <div className="divide-y divide-[#F2EBE1]">
          {upcomingItems.map((a) => (a._type === "private" ? (
            <button key={`p-${a.id}`} onClick={() => navigate("/admin/agenda")} data-testid={`dash-private-${a.id}`}
              className="w-full flex items-center justify-between gap-3 py-3 -mx-2 px-2 rounded-lg text-left hover:bg-cream-subtle/40 transition-colors group">
              <div className="min-w-0 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: a.color }} />
                <div className="min-w-0">
                  <div className="font-medium truncate">{a.title || "Privé"}</div>
                  <div className="text-sm text-ink-muted flex items-center gap-1"><UserRound className="w-3 h-3" /> Privé afspraak</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="text-sm text-right text-ink-muted">{format(parseISO(a.start), "EEE d MMM · HH:mm", { locale: nl })}</div>
                <ChevronRight className="w-4 h-4 text-ink-muted group-hover:text-brand transition-colors" />
              </div>
            </button>
          ) : (
            <button key={a.id} onClick={() => navigate(`/admin/klanten?klant=${a.customer_id}`)}
              data-testid={`dash-appt-${a.id}`}
              className="w-full flex items-center justify-between gap-3 py-3 -mx-2 px-2 rounded-lg text-left hover:bg-cream-subtle/40 transition-colors group">
              <div className="min-w-0">
                <div className="font-medium truncate">{a.customer_name}</div>
                <div className="text-sm text-ink-muted truncate">{a.treatment_name}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="text-sm text-right text-ink-muted">
                  {format(parseISO(a.start), "EEE d MMM · HH:mm", { locale: nl })}
                </div>
                <ChevronRight className="w-4 h-4 text-ink-muted group-hover:text-brand transition-colors" />
              </div>
            </button>
          )))}
        </div>
      </div>
    </div>
  );
}
